import { useEffect, useState } from 'react';
import type { SelectionAvailability } from '@/types/selection-availability';

interface UseSelectionStateOptions<TWithTeams> {
  pollUrl: string;
  initialStartTime: string | null;
  initialEndTime: string | null;
  initialWithTeams: TWithTeams | null;
  serverTimeNow: () => Date;
}

interface UseSelectionStateResult<TWithTeams> {
  startTime: Date | null;
  endTime: Date | null;
  selectionStarted: boolean;
  selectionClosed: boolean;
  withTeams: TWithTeams | null;
}

const POLL_INTERVAL_MS = 3000;
const TICK_INTERVAL_MS = 1000;

export function useSelectionState<TWithTeams>({
  pollUrl,
  initialStartTime,
  initialEndTime,
  initialWithTeams,
  serverTimeNow,
}: UseSelectionStateOptions<TWithTeams>): UseSelectionStateResult<TWithTeams> {
  const [startTime, setStartTime] = useState<Date | null>(
    initialStartTime ? new Date(initialStartTime) : null,
  );
  const [endTime, setEndTime] = useState<Date | null>(
    initialEndTime ? new Date(initialEndTime) : null,
  );
  const [selectionStarted, setSelectionStarted] = useState(
    () => startTime !== null && serverTimeNow() >= startTime,
  );
  const [selectionClosed, setSelectionClosed] = useState(
    () => endTime !== null && serverTimeNow() >= endTime,
  );
  const [withTeams, setWithTeams] = useState<TWithTeams | null>(
    initialWithTeams,
  );

  // Local clock tick: flips selectionStarted/selectionClosed as we cross the
  // start and end times.
  useEffect(() => {
    if (selectionStarted && selectionClosed) {
      return;
    }

    const tick = () => {
      const now = serverTimeNow();
      if (startTime && now >= startTime) {
        setSelectionStarted(true);
      }
      if (endTime && now >= endTime) {
        setSelectionClosed(true);
      }
    };

    tick();
    const interval = setInterval(tick, TICK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [startTime, endTime, selectionStarted, selectionClosed, serverTimeNow]);

  // Single poller covering all phases:
  //   pre-start: watch for start/end time updates from admins.
  //   open: keep window times fresh.
  //   post-close: watch for the closed overview (withTeams) to arrive.
  // Stops once selection is closed and the overview is in hand.
  useEffect(() => {
    if (selectionClosed && withTeams) {
      return;
    }

    const poll = async () => {
      try {
        const response = await fetch(pollUrl);
        if (!response.ok) return;
        const data =
          (await response.json()) as SelectionAvailability<TWithTeams>;

        if (data.selectionStartTime) {
          const remoteStart = new Date(data.selectionStartTime);
          setStartTime((current) =>
            !current || current.getTime() !== remoteStart.getTime()
              ? remoteStart
              : current,
          );
        } else {
          setStartTime(null);
        }

        if (data.selectionEndTime) {
          const remoteEnd = new Date(data.selectionEndTime);
          setEndTime((current) =>
            !current || current.getTime() !== remoteEnd.getTime()
              ? remoteEnd
              : current,
          );
        } else {
          setEndTime(null);
        }

        if (data.selectionClosed && data.withTeams) {
          setWithTeams(data.withTeams);
        }
      } catch (error) {
        console.error('Error polling selection availability:', error);
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [pollUrl, selectionClosed, withTeams]);

  return { startTime, endTime, selectionStarted, selectionClosed, withTeams };
}
